import { excludedSpriteIds, itemSpriteRanges } from '../data/guide/itemSpriteIndex'
import { ITEM_SHEET_GRID, sheetUrl } from '../data/guide/itemSpriteSheets'
import { itemSpriteBounds } from '../data/guide/itemSpriteBounds'
import type { SpriteBounds } from '../data/guide/itemSpriteBounds'

export type SpriteCell = { sheet: number; column: number; row: number }
/** bounds: rectángulo visible del encuadre (celda de referencia 256); sin él se muestra la celda completa. */
export type SpriteRef = SpriteCell & { url: string; itemId: string; isBase: boolean; bounds?: SpriteBounds }

const PER_SHEET = ITEM_SHEET_GRID * ITEM_SHEET_GRID

/** item_id (texto exacto) → celda, en el orden del índice. */
export const spriteIndex: Map<string, SpriteCell> = (() => {
  const m = new Map<string, SpriteCell>()
  let i = 0
  for (const [section, ranges] of itemSpriteRanges) {
    for (const [a, b] of ranges) {
      for (let n = a; n <= b; n++) {
        const local = i % PER_SHEET
        m.set(`${section},${n}`, { sheet: Math.floor(i / PER_SHEET) + 1, row: Math.floor(local / ITEM_SHEET_GRID), column: local % ITEM_SHEET_GRID })
        i++
      }
    }
  }
  return m
})()

const excluded = new Set(excludedSpriteIds)

/**
 * Imagen de un ítem por ID exacto. Si es una variante (id@n) sin imagen propia, usa la del ítem base
 * y lo indica con isBase (se muestra "Imagen del ítem base"). Los excluidos nunca reciben imagen.
 */
export function spriteForItem(id: string | undefined): SpriteRef | undefined {
  if (!id) return undefined
  const exact = id.trim()
  const baseId = exact.split('@')[0]
  if (excluded.has(exact) || excluded.has(baseId)) return undefined
  const own = spriteIndex.get(exact)
  const cell = own ?? (baseId !== exact ? spriteIndex.get(baseId) : undefined)
  if (!cell) return undefined
  const url = sheetUrl(cell.sheet)
  if (!url) return undefined
  const itemId = own ? exact : baseId
  return { ...cell, url, itemId, isBase: !own, bounds: itemSpriteBounds[itemId] }
}

/*
 * Estado de carga compartido por lámina: una sola descarga por URL, avisando a todos los sprites.
 * "ok" solo cuando la imagen se descargó Y se decodificó por completo con un tamaño real mayor que 0.
 * Una falla de carga es "error" (nunca "ítem sin imagen").
 */
export type SheetStatus = 'loading' | 'ok' | 'error'
export type SheetInfo = { status: SheetStatus; width?: number; height?: number; error?: string }
const info = new Map<string, SheetInfo>()
const listeners = new Map<string, Set<(s: SheetStatus) => void>>()

export const sheetInfo = (url: string): SheetInfo | undefined => info.get(url)

function settle(url: string, next: SheetInfo) {
  info.set(url, next)
  listeners.get(url)?.forEach((f) => f(next.status))
}

export function watchSheet(url: string, cb: (s: SheetStatus) => void): () => void {
  if (!listeners.has(url)) listeners.set(url, new Set())
  listeners.get(url)!.add(cb)
  const current = info.get(url)
  if (current) cb(current.status)
  else {
    info.set(url, { status: 'loading' })
    const img = new Image()
    img.onload = async () => {
      try {
        if (typeof img.decode === 'function') await img.decode()
      } catch {
        settle(url, { status: 'error', error: 'Se descargó pero no se pudo decodificar' })
        return
      }
      if (!img.naturalWidth || !img.naturalHeight) settle(url, { status: 'error', error: 'Imagen vacía (0 px)' })
      else settle(url, { status: 'ok', width: img.naturalWidth, height: img.naturalHeight })
    }
    img.onerror = () => settle(url, { status: 'error', error: 'No se pudo descargar' })
    img.src = url
  }
  return () => { listeners.get(url)?.delete(cb) }
}
